import { createFileRoute } from "@tanstack/react-router";
import { BookingProvider } from "@/components/site/booking-context";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import {
  AccommodationsHero,
  AccommodationsIntro,
  SiteTypeNav,
  PullThroughSites,
  BackInSites,
  AccessibleSites,
  SiteIncludes,
  AccommodationsFitTool,
  StayOptions,
  DestinationBridge,
  AccommodationsFAQ,
  AccommodationsCTA,
} from "@/components/site/accommodations";
import { heroPreloadLink } from "@/data/optimized-images";
import { accommodationFaqs, resort } from "@/data/resort";
import { absoluteUrl } from "@/lib/site";
import heroMainAsset from "@/assets/auburn-hero-main.webp.asset.json";


const title = "RV Sites & Accommodations in Auburn, CA | Auburn RV Resort";
const description =
  "Explore pull-through, back-in and accessible RV sites at Auburn RV Resort in Auburn, California. Full hookups, 20/30/50 amp service, WiFi and sites up to 120 feet.";
const ogImage = `https://auburnrvresort.com${heroMainAsset.url}`;


const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LodgingBusiness", "Campground"],
      name: resort.name,
      description,
      telephone: resort.phone,
      email: resort.email,
      url: "/accommodations/",
      address: {
        "@type": "PostalAddress",
        streetAddress: resort.address.street,
        addressLocality: resort.address.city,
        addressRegion: resort.address.state,
        postalCode: resort.address.zip,
        addressCountry: "US",
      },
      amenityFeature: [
        "Full Hookups",
        "20/30/50 Amp Service",
        "Pull-Through Sites",
        "Accessible Sites",
        "Complimentary WiFi",
        "Picnic Tables",
      ].map((name) => ({
        "@type": "LocationFeatureSpecification",
        name,
        value: true,
      })),
    },
    {
      "@type": "FAQPage",
      mainEntity: accommodationFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export const Route = createFileRoute("/accommodations")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1322" },
      { property: "og:image:height", content: "936" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/accommodations/") }, heroPreloadLink],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  }),
  component: AccommodationsPage,
});

function AccommodationsPage() {
  return (
    <BookingProvider>
      <Navbar />
      <main>
        <AccommodationsHero />
        <AccommodationsIntro />
        <SiteTypeNav />
        <PullThroughSites />
        <BackInSites />
        <AccessibleSites />
        <SiteIncludes />
        <AccommodationsFitTool />
        <StayOptions />
        <DestinationBridge />
        <AccommodationsFAQ />
        <AccommodationsCTA />
      </main>
      <Footer />
      <MobileBookingBar />
    </BookingProvider>
  );
}
